import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  MenuGroup,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Stack,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  useDisclosure,
  RadioGroup,
  Radio,
} from '@chakra-ui/react';
import { 
  FiSearch, 
  FiMoreVertical, 
  FiEye, 
  FiEdit, 
  FiTrash2, 
  FiCheck, 
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiMessageSquare,
  FiRefreshCw
} from 'react-icons/fi';
import candidateService from '../../services/api/candidateService';
import config from '../../config/config';
import notificationService from '../../services/api/notificationService';
import { getNominationStatusLabel, getNominationStatusColor } from '../../utils/evaluationStatusUtils';
import usePersistentState from '../../hooks/usePersistentState';

/**
 * CandidatesList component
 * Admin page for managing candidate applications
 */
const CandidatesList = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { currentUser } = useAuth();
  
  // State
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  // Persist filters in sessionStorage
  const [searchTerm, setSearchTerm] = usePersistentState('adminCandidates.search', '');
  const [statusFilter, setStatusFilter] = usePersistentState('adminCandidates.status', 'all');
  const [branchFilter, setBranchFilter] = usePersistentState('adminCandidates.branch', currentUser?.branch?.id || 'all');
  const [centerFilter, setCenterFilter] = usePersistentState('adminCandidates.center', 'all');
  // Persist current page in sessionStorage
  const [currentPage, setCurrentPage] = usePersistentState('adminCandidates.page', 1);
  // Persist per-page in localStorage
  const [itemsPerPage, setItemsPerPage] = usePersistentState('adminCandidates.perPage', 10, 'local');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [smsMessage, setSmsMessage] = useState('');
  const [isSendingSms, setIsSendingSms] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [hasEval, setHasEval] = useState(false);
  const [evalCount, setEvalCount] = useState(0);
  
  // Modal controls
  const { 
    isOpen: isSmsModalOpen, 
    onOpen: onSmsModalOpen, 
    onClose: onSmsModalClose 
  } = useDisclosure();
  
  const { 
    isOpen: isStatusModalOpen, 
    onOpen: onStatusModalOpen, 
    onClose: onStatusModalClose 
  } = useDisclosure();
  
  // Pagination
  const totalPages = Math.ceil(filteredCandidates.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCandidates = filteredCandidates.slice(startIndex, endIndex);

  // Centralized helpers
  const getStatusColorScheme = (candidate) => getNominationStatusColor(candidate?.status);
  const getStatusLabel = (candidate) => getNominationStatusLabel(candidate?.status);

  // Determine if any filter is active
  const isAnyFilterActive = (
    (searchTerm && searchTerm.trim() !== '') ||
    statusFilter !== 'all' ||
    branchFilter !== 'all' ||
    centerFilter !== 'all'
  );

  // Clear all filters and reset pagination
  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setBranchFilter('all');
    setCenterFilter('all');
    setCurrentPage(1);
  };

  // Set default branch filter based on committee user's branch
  useEffect(() => {
    if (currentUser?.role === 'committee') {
      // Set branch filter based on available branch data
      if (currentUser.branch?.id) {
        // Ensure we set the branch ID, not the whole object
        setBranchFilter(currentUser.branch.id);
      } else if (currentUser.branch_id) {
        setBranchFilter(currentUser.branch_id);
      }
    }
  }, [currentUser, setBranchFilter]);

  // Fetch candidates data
  useEffect(() => {
    const fetchCandidates = async () => {
      try {
        setLoading(true);
        
        // Use branches and centers from config.js instead of API calls
        const branches = config.organization.branches;
        const centers = config.organization.centers;
        
        // Fetch candidates using candidateService
        const response = await candidateService.getAllCandidates();
        
        if (response && response.data) {
          // Enrich candidate data with branch and center names
          const enrichedCandidates = response.data.map(candidate => {
            // Handle branch data
            let branchId = candidate.branch;
            let branchName = '';
            
            if (candidate.branch && typeof candidate.branch === 'object' && candidate.branch.id) {
              branchId = candidate.branch.id;
              branchName = candidate.branch.name || '';
            } else if (candidate.branch_name) {
              branchName = candidate.branch_name;
            } else {
              const branchConfig = branches.find(b => b.id === branchId);
              branchName = branchConfig ? branchConfig.name : branchId;
            }
            
            // Handle center data
            let centerId = candidate.center;
            let centerName = '';
            
            if (candidate.center && typeof candidate.center === 'object' && candidate.center.id) {
              centerId = candidate.center.id;
              centerName = candidate.center.name || '';
            } else if (candidate.center_name) {
              centerName = candidate.center_name;
            } else {
              const centerConfig = centers.find(c => c.id === centerId);
              centerName = centerConfig ? centerConfig.name : centerId;
            }
            
            return {
              ...candidate,
              branchId: branchId,
              branchName: branchName,
              centerId: centerId,
              centerName: centerName
            };
          });
          
          setCandidates(enrichedCandidates);
          setFilteredCandidates(enrichedCandidates);
        } else {
          throw new Error('Failed to fetch candidates');
        }
      } catch (error) {
        toast({
          title: 'خطأ في جلب البيانات',
          description: error.message || 'حدث خطأ أثناء جلب بيانات المرشحين',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCandidates();
  }, [toast]);

  // Apply filters
  useEffect(() => {
    let filtered = [...candidates];
    
    // Apply search term filter
    if (searchTerm) {
      filtered = filtered.filter(
        (candidate) => {
          // Handle different data structures
          const firstName = candidate.personal_info?.first_name || candidate.first_name || '';
          const lastName = candidate.personal_info?.last_name || candidate.last_name || '';
          const email = candidate.personal_info?.email || candidate.email || '';
          const nationalId = candidate.personal_info?.national_id || candidate.national_id || '';
          
          return firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            nationalId.toString().includes(searchTerm);
        }
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((candidate) => candidate.status === statusFilter);
    }
    
    // Apply branch filter
    if (branchFilter !== 'all') {
      filtered = filtered.filter(candidate => candidate.branchId === branchFilter);
    }
    
    // Apply center filter
    if (centerFilter !== 'all') {
      filtered = filtered.filter(candidate => candidate.centerId === centerFilter);
    }
    
    setFilteredCandidates(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchTerm, statusFilter, branchFilter, centerFilter, candidates, setCurrentPage]);

  // Handle view candidate
  const handleViewCandidate = (candidate) => {
    navigate(`/admin/candidates/${candidate.id}`);
  };

  // Handle edit candidate
  const handleEditCandidate = (candidate) => {
    navigate(`/admin/candidates/${candidate.id}/edit`);
  };

  // Handle delete candidate
  const handleDeleteCandidate = async (candidate) => {
    setSelectedCandidate(candidate);
    try {
      // Check if candidate has evaluations
      const check = await candidateService.hasEvaluations(candidate.id);
      setHasEval(!!check?.has_evaluations);
      setEvalCount(check?.count || 0);
    } catch (_e) {
      // On failure, default to no extra info
      setHasEval(false);
      setEvalCount(0);
    }
    setDeleteConfirmOpen(true);
  };

  // Confirm delete candidate
  const confirmDeleteCandidate = async () => {
    try {
      const response = await candidateService.deleteCandidate(selectedCandidate.id);
      
      if (response && response.success) {
        setCandidates(candidates.filter((c) => c.id !== selectedCandidate.id));
        setFilteredCandidates(prev => prev.filter(c => c.id !== selectedCandidate.id));
        
        toast({
          title: 'تم الحذف بنجاح',
          description: 'تم حذف المرشح بنجاح',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
        
        setDeleteConfirmOpen(false);
      } else {
        throw new Error(response?.message || 'Failed to delete candidate');
      }
    } catch (error) {
      toast({
        title: 'خطأ في الحذف',
        description: error.message || 'حدث خطأ أثناء حذف المرشح',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  // Open status change modal
  const openStatusChangeModal = (candidate) => {
    setSelectedCandidate(candidate);
    setNewStatus(candidate.status || '');
    onStatusModalOpen();
  };

  // Handle change status
  const handleChangeStatus = async (candidate, status, sendNotification = false) => {
    try {
      setLoading(true);
      // Use the proper updateCandidateStatus endpoint with notes parameter
      const response = await candidateService.updateCandidateStatus(
        candidate.id,
        status,
        `تم تحديث الحالة بواسطة ${currentUser?.role === 'admin' ? 'المسؤول' : 'عضو اللجنة'}`
      );
      
      if (response) {
        // Update local state
        const updatedCandidates = candidates.map((c) =>
          c.id === candidate.id ? { ...c, status: status } : c
        );
        
        setCandidates(updatedCandidates);
        setFilteredCandidates(prevFilteredCandidates => 
          prevFilteredCandidates.map(c => 
            c.id === candidate.id ? { ...c, status: status } : c
          )
        );
        
        toast({
          title: 'تم تحديث الحالة',
          description: `تم تغيير حالة المرشح إلى ${getNominationStatusLabel(status)}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
        
        // Close modal if it was opened
        if (isStatusModalOpen) {
          onStatusModalClose();
        }
        // Optionally open SMS modal
        if (sendNotification) {
          openSmsModal(candidate);
        }
      } else {
        throw new Error(response?.message || 'Failed to update candidate status');
      }
    } catch (error) {
      toast({
        title: 'خطأ في تحديث الحالة',
        description: error.message || 'حدث خطأ أثناء تحديث حالة المرشح',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Submit status change from modal
  const submitStatusChange = () => {
    if (selectedCandidate && newStatus) {
      handleChangeStatus(selectedCandidate, newStatus, false);
    }
  };

  // Submit status change and open SMS modal
  const submitStatusChangeAndNotify = () => {
    if (selectedCandidate && newStatus) {
      handleChangeStatus(selectedCandidate, newStatus, true);
    }
  };

  // Open SMS modal
  const openSmsModal = (candidate) => {
    setSelectedCandidate(candidate);
    
    // Set default message based on candidate status
    const status = getStatusLabel(candidate);
    const firstName = candidate.personalInfo?.firstName || 
                     candidate.personal_info?.first_name || 
                     candidate.first_name || '';
    const defaultMessage = `مرحباً ${firstName}، تم تحديث حالة طلبك إلى ${status}. للمزيد من المعلومات يرجى زيارة منصة مجالسنا.`;
    
    setSmsMessage(defaultMessage);
    onSmsModalOpen();
  };
  
  // Send SMS notification
  const sendSmsNotification = async () => {
    if (!selectedCandidate || !smsMessage) return;
    
    try {
      setIsSendingSms(true);
      
      const phoneNumber = selectedCandidate.personalInfo?.phone || 
                          selectedCandidate.personal_info?.phone || 
                          selectedCandidate.phone;
      
      if (!phoneNumber) {
        throw new Error('رقم الهاتف غير متوفر للمرشح');
      }
      
      // Use the correct function name from notificationService
      const response = await notificationService.sendCandidateSMS(selectedCandidate, smsMessage);
      
      // Backend returns a successful response with details.replyCode 0 when SMS is sent successfully
      // Check for success in various response formats
      console.log('SMS response:', response);
      if (response && (
        response.success || 
        response.status === 'success' || 
        response.status === 200 || 
        (response.details && response.details.replyCode === 0)
      )) {
        toast({
          title: 'تم إرسال الرسالة',
          description: 'تم إرسال الإشعار بنجاح',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
        
        onSmsModalClose();
      } else {
        throw new Error(response?.message || 'Failed to send SMS notification');
      }
    } catch (error) {
      toast({
        title: 'خطأ في إرسال الإشعار',
        description: error.message || 'حدث خطأ أثناء إرسال الإشعار',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsSendingSms(false);
    }
  };
  
  // Removed handleExportCandidates as it is not available

  // Get branch name
  const getBranchName = (branchId) => {
    const branch = config.organization.branches.find((b) => b.id === branchId);
    return branch ? branch.name : '';
  };

  // Get center name
  const getCenterName = (centerId) => {
    const center = config.organization.centers.find((c) => c.id === centerId);
    return center ? center.name : '';
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA');
  };

  // Close and reset delete modal state
  const closeDeleteModal = () => {
    setDeleteConfirmOpen(false);
    setHasEval(false);
    setEvalCount(0);
    setSelectedCandidate(null);
  };

  return (
    <Container maxW="container.xl" py={8}>
      <Box mb={8}>
        <Heading as="h1" size="lg" mb={2}>
          قائمة المرشحين
        </Heading>
        <Text color="gray.600">
          إدارة طلبات الترشيح ومراجعتها
        </Text>
      </Box>

      {/* Filters */}
      <Box mb={6} p={4} bg="white" borderRadius="lg" boxShadow="sm">
        <Stack spacing={4} direction={{ base: 'column', md: 'row' }}>
          {/* Search */}
          <InputGroup maxW={{ base: '100%', md: '300px' }}>
            <InputLeftElement pointerEvents="none">
              <FiSearch color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="بحث بالاسم أو البريد الإلكتروني أو رقم الهوية"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
          
          {/* Removed export/update button as handleExportCandidates is not available */}

          {/* Status Filter */}
          <Select
            placeholder="تصفية حسب الحالة"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            maxW={{ base: '100%', md: '200px' }}
          >
            <option value="all">جميع الحالات</option>
            <option value="draft">مسودة</option>
            <option value="submitted">تحت التقييم</option>
            <option value="pending">في انتظار النتيجة</option>
            <option value="shortlisted">قبول أولي</option>
            <option value="accepted">مقبول</option>
            <option value="rejected">مرفوض</option>
            <option value="returned">عائد</option>
          </Select>

          {/* Branch Filter */}
          <Select
            placeholder="تصفية حسب الفرع"
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            maxW={{ base: '100%', md: '200px' }}
            isDisabled={currentUser?.role === 'committee'}
          >
            <option value="all">جميع الفروع</option>
            {config.organization.branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>

          {/* Center Filter */}
          <Select
            placeholder="تصفية حسب المركز"
            value={centerFilter}
            onChange={(e) => setCenterFilter(e.target.value)}
            maxW={{ base: '100%', md: '200px' }}
            isDisabled={branchFilter === 'all'}
          >
            <option value="all">جميع المراكز</option>
            {config.organization.centers
              .filter((center) => branchFilter === 'all' || String(center.branchId) === String(branchFilter))
              .map((center) => (
                <option key={center.id} value={center.id}>
                  {center.name}
                </option>
              ))}
          </Select>
        
        {/* Clear Filters Button */}
        <Button
          leftIcon={<FiX />}
          colorScheme={isAnyFilterActive ? 'red' : 'gray'}
          variant={isAnyFilterActive ? 'solid' : 'outline'}
          onClick={clearAllFilters}
          ml={{ base: 0, md: 'auto' }}
          title={isAnyFilterActive ? 'هناك عوامل تصفية مفعّلة - انقر لمسحها' : 'لا توجد عوامل تصفية مفعّلة'}
        >
          مسح التصفية
        </Button>
      </Stack>
    </Box>

      {/* Candidates Table */}
      <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th>الاسم</Th>
              <Th>رقم الهوية</Th>
              <Th>الفرع</Th>
              <Th>المركز</Th>
              <Th>تاريخ التقديم</Th>
              <Th>الحالة</Th>
              <Th>الإجراءات</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={7} textAlign="center" py={10}>
                  جاري تحميل البيانات...
                </Td>
              </Tr>
            ) : currentCandidates.length === 0 ? (
              <Tr>
                <Td colSpan={7} textAlign="center" py={10}>
                  لا توجد بيانات متاحة
                </Td>
              </Tr>
            ) : (
              currentCandidates.map((candidate) => (
                <Tr key={candidate.id}>
                  <Td>
                    {candidate.personal_info ? 
                      `${candidate.personal_info.first_name} ${candidate.personal_info.last_name}` : 
                      `${candidate.first_name} ${candidate.last_name}`
                    }
                  </Td>
                  <Td>{candidate.personal_info ? candidate.personal_info.national_id : candidate.national_id}</Td>
                  <Td>{getBranchName(candidate.nomination ? candidate.nomination.branch : candidate.branch)}</Td>
                  <Td>
                    {candidate.centerName ||
                      getCenterName(
                        candidate.centerId ||
                        (candidate.nomination ? candidate.nomination.center : candidate.center)
                      )}
                  </Td>
                  <Td>{formatDate(candidate.submission_date || candidate.application_date)}</Td>
                  <Td>
                    <Badge colorScheme={getStatusColorScheme(candidate)} px={2} py={1} borderRadius="full">
                      {getStatusLabel(candidate)}
                    </Badge>
                  </Td>
                  <Td>
                    <Flex gap={2}>
                      {/* Only show view details icon */}
                      <IconButton
                        icon={<FiEye />}
                        variant="ghost"
                        size="sm"
                        colorScheme="teal"
                        aria-label="عرض التفاصيل"
                        onClick={() => handleViewCandidate(candidate)}
                        title="عرض التفاصيل"
                      />
                      
                      {/* More options menu */}
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<FiMoreVertical />}
                          variant="ghost"
                          size="sm"
                          aria-label="المزيد من الخيارات"
                        />
                        <MenuList>
                          <MenuGroup title="الإجراءات الأساسية">
                            <MenuItem icon={<FiEye />} onClick={() => handleViewCandidate(candidate)}>
                              عرض التفاصيل
                            </MenuItem>
                            <MenuItem icon={<FiEdit />} onClick={() => handleEditCandidate(candidate)}>
                              تعديل
                            </MenuItem>
                            {/* Download option hidden as requested */}
                          </MenuGroup>
                          
                          <MenuDivider />
                          
                          <MenuGroup title="تغيير الحالة">
                            <MenuItem icon={<FiRefreshCw />} onClick={() => openStatusChangeModal(candidate)}>
                              تغيير الحالة
                            </MenuItem>
                            <MenuItem icon={<FiCheck />} onClick={() => handleChangeStatus(candidate, 'accepted')}>
                              قبول
                            </MenuItem>
                            <MenuItem icon={<FiX />} onClick={() => handleChangeStatus(candidate, 'rejected')}>
                              رفض
                            </MenuItem>
                          </MenuGroup>
                          
                          <MenuDivider />
                          
                          <MenuGroup title="الإشعارات">
                            <MenuItem 
                              icon={<FiMessageSquare />} 
                              onClick={() => openSmsModal(candidate)}
                            >
                              إرسال إشعار SMS
                            </MenuItem>
                          </MenuGroup>
                          
                          <MenuDivider />
                          
                          {currentUser?.role === 'admin' && (
                            <MenuItem icon={<FiTrash2 />} onClick={() => handleDeleteCandidate(candidate)} color="red.500">
                              حذف
                            </MenuItem>
                          )}
                        </MenuList>
                      </Menu>
                    </Flex>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>

        {/* Pagination and page size */}
        {!loading && filteredCandidates.length > 0 && (
          <Flex justify="space-between" align="center" p={4} borderTop="1px" borderColor="gray.200">
            <Text color="gray.600">
              عرض {startIndex + 1} إلى {Math.min(endIndex, filteredCandidates.length)} من أصل {filteredCandidates.length} مرشح
            </Text>
            <Stack direction="row" spacing={3} align="center">
              <Select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                width="90px"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Select>
              <IconButton
                icon={<FiChevronRight />}
                aria-label="الصفحة السابقة"
                isDisabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              />
              <IconButton
                icon={<FiChevronLeft />}
                aria-label="الصفحة التالية"
                isDisabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              />
            </Stack>
          </Flex>
        )}
      </Box>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteConfirmOpen} onClose={closeDeleteModal} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>تأكيد الحذف</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {hasEval && (
              <Box mb={3}>
                <Text color="red.600" fontWeight="bold">
                  تنبيه: لدى هذا المرشح {evalCount} تقييم(ات). سيتم حذف جميع التقييمات المرتبطة مع الحذف.
                </Text>
              </Box>
            )}
            هل أنت متأكد من رغبتك في حذف بيانات المرشح{' '}
            {selectedCandidate && (
              selectedCandidate.personalInfo ? 
              `${selectedCandidate.personalInfo.firstName} ${selectedCandidate.personalInfo.lastName}` :
              `${selectedCandidate.first_name || ''} ${selectedCandidate.last_name || ''}`
            )}؟
            <Text color="red.500" mt={2}>
              هذا الإجراء لا يمكن التراجع عنه.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Stack direction="row-reverse" spacing={3} w="100%" justify="flex-start">
              <Button colorScheme="red" onClick={confirmDeleteCandidate}>
                تأكيد الحذف
              </Button>
              <Button variant="outline" onClick={closeDeleteModal}>
                إلغاء
              </Button>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {/* SMS Notification Modal */}
      <Modal isOpen={isSmsModalOpen} onClose={onSmsModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>إرسال إشعار SMS</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>الرسالة</FormLabel>
              <Textarea 
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                placeholder="أدخل نص الرسالة هنا..."
                rows={5}
              />
            </FormControl>
            <Text fontSize="sm" color="gray.500" mt={2}>
              سيتم إرسال الرسالة إلى رقم الهاتف المسجل للمرشح
            </Text>
          </ModalBody>
          <ModalFooter>
            <Stack direction="row-reverse" spacing={3} w="100%" justify="flex-start">
              <Button 
                colorScheme="blue" 
                onClick={sendSmsNotification}
                isLoading={isSendingSms}
                isDisabled={!smsMessage}
              >
                إرسال
              </Button>
              <Button variant="outline" onClick={onSmsModalClose}>
                إلغاء
              </Button>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {/* Change Status Modal */}
      <Modal isOpen={isStatusModalOpen} onClose={onStatusModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>تغيير حالة المرشح</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>اختر الحالة الجديدة</FormLabel>
              <RadioGroup value={newStatus} onChange={setNewStatus}>
                <Stack spacing={3}>
                  <Radio value="draft">مسودة</Radio>
                  <Radio value="submitted">تحت التقييم</Radio>
                  <Radio value="pending">في انتظار النتيجة</Radio>
                  <Radio value="shortlisted">قبول أولي</Radio>
                  <Radio value="accepted">مقبول</Radio>
                  <Radio value="rejected">مرفوض</Radio>
                  <Radio value="returned">عائد</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Stack direction="row-reverse" spacing={3} w="100%" justify="flex-start">
              <Button 
                colorScheme="teal" 
                onClick={submitStatusChangeAndNotify}
                isDisabled={!newStatus}
              >
                تحديث الحالة وإرسال إشعار
              </Button>
              <Button 
                colorScheme="blue" 
                onClick={submitStatusChange}
                isDisabled={!newStatus}
              >
                تحديث الحالة
              </Button>
              <Button variant="outline" onClick={onStatusModalClose}>
                إلغاء
              </Button>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default CandidatesList;
